/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor} from '../../../theme/theme';
import {getAssetDetailsContentStyles} from './assetDetailsContentStyles';

export interface SpreaderStatTileProps {
  label: string;
  value: string;
  // the value's unit, shown smaller after it (e.g. `lbs/mi`) ...
  unit?: string;
  // sizes the tile to its content instead of an even share of its row ...
  fitContent?: boolean;
}

const useStyles = makeStyles(() => {
  const {unit: contentUnit} = getAssetDetailsContentStyles();
  return {
    tile : {
      boxSizing       : 'border-box',
      display         : 'flex',
      flex            : '1 0 0',
      minWidth        : 0,
      minHeight       : '64px',
      alignItems      : 'center',
      padding         : '8px',
      backgroundColor : dlsColor('white'),
      border          : `1px solid ${dlsColor('grey7')}`,
      borderRadius    : '8px'
    },
    contents : {
      display       : 'flex',
      width         : '100%',
      minWidth      : 0,
      flexDirection : 'column',
      gap           : '8px'
    },
    label : {
      overflow      : 'hidden',
      whiteSpace    : 'nowrap',
      textOverflow  : 'ellipsis',
      fontFamily    : 'Roboto',
      fontWeight    : 700,
      fontSize      : '10px',
      lineHeight    : '14px',
      letterSpacing : '1px',
      textTransform : 'uppercase',
      color         : dlsColor('grey3')
    },
    value : {
      overflow     : 'hidden',
      whiteSpace   : 'nowrap',
      textOverflow : 'ellipsis',
      fontFamily   : 'Inter',
      fontWeight   : 500,
      fontSize     : '18px',
      lineHeight   : '22px',
      color        : dlsColor('grey2')
    },
    // a content-sized tile - its row splits only the remaining width ...
    fitContent : {
      flex : '1 1 auto'
    },
    valueGroup : {
      display    : 'flex',
      minWidth   : 0,
      alignItems : 'baseline',
      gap        : '4px'
    },
    unit : {
      ...contentUnit,
      flexShrink : 0
    }
  };
});

/**
 * SpreaderStatTile renders a single bordered stat tile (label above value)
 * for the Spreader accordion's top stat tile row - shared by every current
 * theme manufacturer layout (`spreaderCurrentLayouts`): Force America
 * Type 1 (Granular Set Rate, Spread Rate Index, Spinner Rate) and Type 2,
 * Dickey-john Type 2 (Target/Actual Application Rate, Gate Height) and
 * Cirus/RoadWatch Type 3. `DiagnosticTile`
 * (`@ion/dls/src/components/assetdetails/current/DiagnosticTile`) did not
 * exist in this working tree at implementation time (ION-7462 had not yet
 * landed it), so this is a standalone tile rather than a reuse/extension of
 * that component.
 *
 * Per the Figma spec the tile's own width (130.67px for a 3-tile row,
 * 204px for a 2-tile row) is simply its share of the row's available width
 * split evenly with a fixed gap - so this tile uses `flex: 1 0 0` rather
 * than a hardcoded pixel width, and `SpreaderStatTileRow` supplies the gap.
 * `fitContent` (passed through from `SpreaderStatTileRow`'s own prop of the
 * same name) switches that to `flex: 1 1 auto` - a content-sized tile
 * rather than an even split, used by Dickey-john's three top tiles.
 *
 * Per the Figma mockup, a tile whose value is legitimately "N/A" or "0"
 * still renders that value - callers only omit a tile (by leaving it out
 * of the `tiles` array passed to `SpreaderStatTileRow`) when the underlying
 * measurement is truly absent/undefined. `unit` renders the value's unit
 * (e.g. `lbs/mi`), smaller, right after it, when the reading has one.
 *
 * ```typescript
 *
 *   import {SpreaderStatTile} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderStatTile';
 *   ...
 *
 *   <SpreaderStatTile label='Granular Target Rate'
 *     value='500.0001'
 *     unit='lbs/mi' />
 *
 * ```
 *
 * @param   label      The tile's label.
 * @param   value      The tile's value.
 * @param   unit       The value's unit, shown smaller after it, if any.
 * @param   fitContent Sizes the tile to its content when `true`.
 * @returns            The stat tile.
 */
export const SpreaderStatTile = ({label, value, unit, fitContent = false}: SpreaderStatTileProps) => {
  const classes = useStyles();
  return (
    <div className={classnames(classes.tile, fitContent && classes.fitContent)}>
      <div className={classes.contents}>
        <span className={classes.label}>
          {label}
        </span>
        <span className={classes.valueGroup}>
          <span className={classes.value}>
            {value}
          </span>
          {unit && (
            <span className={classes.unit}>
              {unit}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default memo(SpreaderStatTile);
